import { useState, useCallback, memo } from "react"
import { useMutation, useQuery } from "@apollo/client"
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Table,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react"
import { FaBan, FaCheck, FaEnvelope, FaEye } from "react-icons/fa6"
import {
  GetAllUsersDocument,
  GetAllOrganizationsDocument,
  AddUserToOrganizationDocument,
  InviteUserToOrganizationDocument,
  GetAllInvitationsDocument,
  CancelInvitationDocument,
  ResendInvitationDocument,
} from "@hydrologiq/gql-experience/graphql"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { CreateButton, AssignButton } from "@hydrologiq/shared/content/components/Button"
import { authClient } from "../../auth/auth"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { CancelButton } from "@hydrologiq/shared/content/components/Button"

const GET_ALL_USERS = GetAllUsersDocument
const GET_ALL_ORGANIZATIONS = GetAllOrganizationsDocument

// CREATE_USER mutation REMOVED - now using invitation-first approach

const ADD_USER_TO_ORGANIZATION = AddUserToOrganizationDocument
const INVITE_USER_TO_ORGANIZATION = InviteUserToOrganizationDocument

// BAN_USER and UNBAN_USER mutations REMOVED - now using Better Auth admin API directly

// Memoized dialog content components with internal state management
const InviteUserDialogContent = memo<{
  organizationCollection: any
  creating: boolean
  onSubmit: (user: { email: string; name: string; organizationId: string; organizationRole: string }) => void
  onCancel: () => void
}>(({ organizationCollection, creating, onSubmit, onCancel }) => {
  // State lives inside this component - parent doesn't re-render when typing
  const [localUser, setLocalUser] = useState({ email: "", name: "", organizationId: "", organizationRole: "member" })

  const handleSubmit = useCallback(() => {
    onSubmit(localUser)
  }, [localUser, onSubmit])

  return (
    <VStack gap={4} align="stretch" width="100%">
      <Text fontSize="sm" color="fg.muted">
        An invitation will be sent to this email address. The user will need to accept the invitation and create their
        account using the provided email.
      </Text>
      <Box>
        <Text fontSize="sm" mb={2}>
          Email
        </Text>
        <Input
          value={localUser.email}
          onChange={(e) => setLocalUser((prev) => ({ ...prev, email: e.target.value }))}
          placeholder="user@example.com"
          type="email"
        />
      </Box>
      <Box>
        <Text fontSize="sm" mb={2}>
          Organisation (Required)
        </Text>

        <NativeSelectRoot>
          <NativeSelectField
            value={localUser.organizationId}
            onChange={(e) => setLocalUser((prev) => ({ ...prev, organizationId: e.target.value }))}
            items={[
              { label: "Select an organization...", value: "" },
              ...organizationCollection.items.map((item: any) => ({ label: item.label, value: item.value })),
            ]}
          />
        </NativeSelectRoot>
      </Box>
      <Box>
        <Text fontSize="sm" mb={2}>
          Organisation Role
        </Text>
        <NativeSelectRoot>
          <NativeSelectField
            value={localUser.organizationRole}
            onChange={(e) => setLocalUser((prev) => ({ ...prev, organizationRole: e.target.value }))}
            items={[
              { label: "Member", value: "member" },
              { label: "Owner", value: "owner" },
            ]}
          />
        </NativeSelectRoot>
      </Box>

      <HStack width={"100%"} flexDirection={"row-reverse"}>
        <CreateButton onClick={handleSubmit} loading={creating}>
          Send Invitation
        </CreateButton>
        <CancelButton onClick={onCancel} />
      </HStack>
    </VStack>
  )
})

InviteUserDialogContent.displayName = "InviteUserDialogContent"

const AddToOrganizationDialogContent = memo<{
  selectedUser: any
  organizationCollection: any
  inviting: boolean
  adding: boolean
  onSubmit: (organizationId: string, action: "invite" | "add") => void
  onCancel: () => void
}>(({ selectedUser, organizationCollection, inviting, adding, onSubmit, onCancel }) => {
  // State lives inside this component
  const [localOrganizationId, setLocalOrganizationId] = useState("")
  const [action, setAction] = useState<"invite" | "add">("invite")

  const handleSubmit = useCallback(() => {
    onSubmit(localOrganizationId, action)
  }, [localOrganizationId, action, onSubmit])

  const isLoading = inviting || adding

  return (
    <VStack gap={4} align="stretch" width="100%">
      <Text>
        Add <strong>{selectedUser?.name}</strong> to an organization
      </Text>
      <Box>
        <Text fontSize="sm" mb={2}>
          Action
        </Text>
        <NativeSelect.Root>
          <NativeSelect.Field value={action} onChange={(e) => setAction(e.target.value as "invite" | "add")}>
            <option value="invite">Invite (user must accept)</option>
            <option value="add">Add directly (no invitation needed)</option>
          </NativeSelect.Field>
        </NativeSelect.Root>
        <Text fontSize="xs" mt={1} color="fg.muted">
          {action === "invite"
            ? "User will receive an invitation and must accept it"
            : "User will be added immediately without needing to accept"}
        </Text>
      </Box>
      <Box>
        <Text fontSize="sm" mb={2}>
          Select Organisation
        </Text>
        <NativeSelect.Root>
          <NativeSelect.Field value={localOrganizationId} onChange={(e) => setLocalOrganizationId(e.target.value)}>
            <option value="">Select an organization...</option>
            {organizationCollection.items.map((item: any) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </NativeSelect.Field>
        </NativeSelect.Root>
      </Box>
      <HStack width={"100%"} flexDirection={"row-reverse"}>
        <AssignButton onClick={handleSubmit} loading={isLoading} disabled={!localOrganizationId || isLoading}>
          {action === "invite" ? "Invite to Organization" : "Add to Organization"}
        </AssignButton>
        <CancelButton onClick={onCancel} disabled={isLoading} />
      </HStack>
    </VStack>
  )
})

AddToOrganizationDialogContent.displayName = "AddToOrganizationDialogContent"

const BanUserDialogContent = memo<{
  selectedUser: any
  banning: boolean
  onSubmit: (reason: string) => void
  onCancel: () => void
}>(({ selectedUser, banning, onSubmit, onCancel }) => {
  // State lives inside this component
  const [localBanReason, setLocalBanReason] = useState("")

  const handleSubmit = useCallback(() => {
    onSubmit(localBanReason)
  }, [localBanReason, onSubmit])

  return (
    <VStack gap={4} align="stretch" width="100%">
      <Text>
        Are you sure you want to ban <strong>{selectedUser?.name}</strong>?
      </Text>
      <Box>
        <Text fontSize="sm" mb={2}>
          Reason (Optional)
        </Text>
        <Input
          value={localBanReason}
          onChange={(e) => setLocalBanReason(e.target.value)}
          placeholder="Enter ban reason"
        />
      </Box>
      <HStack width={"100%"} flexDirection={"row-reverse"}>
        <Button variant="warning" onClick={handleSubmit} loading={banning}>
          Ban User
        </Button>
        <CancelButton onClick={onCancel} />
      </HStack>
    </VStack>
  )
})

BanUserDialogContent.displayName = "BanUserDialogContent"

export const UserManager = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showAddToOrganizationDialog, setShowAddToOrganizationDialog] = useState(false)
  const [showBanDialog, setShowBanDialog] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [filterTenant, setFilterTenant] = useState<string>("")
  const [showInvitations, setShowInvitations] = useState(false)
  const [selectedOrgForInvitations, setSelectedOrgForInvitations] = useState<string>("")

  // Loading states for Better Auth operations
  const [creating, setCreating] = useState(false)
  const [banning, setBanning] = useState(false)
  const [unbanning, setUnbanning] = useState(false)

  const { data: usersData, loading: usersLoading, refetch: refetchUsers } = useQuery(GET_ALL_USERS)
  const { data: organizationsData } = useQuery(GET_ALL_ORGANIZATIONS)

  // Invitation management queries
  const {
    data: invitationsData,
    loading: invitationsLoading,
    refetch: refetchInvitations,
  } = useQuery(GetAllInvitationsDocument, {
    variables: {
      filter: selectedOrgForInvitations ? { organizationId: selectedOrgForInvitations } : null,
    },
    skip: !showInvitations,
  })

  const [addUserToOrganization, { loading: adding }] = useMutation(ADD_USER_TO_ORGANIZATION, {
    onCompleted: () => {
      toaster.success({ title: "User added to organization" })
      setShowAddToOrganizationDialog(false)
      setSelectedUser(null)
      refetchUsers()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to add user to organization", description: error.message })
    },
  })

  const [inviteUserToOrganization, { loading: inviting }] = useMutation(INVITE_USER_TO_ORGANIZATION, {
    onCompleted: () => {
      toaster.success({ title: "Invitation sent" })
      setShowAddToOrganizationDialog(false)
      setSelectedUser(null)
      refetchUsers()
      refetchInvitations()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to send invitation", description: error.message })
    },
  })

  const [cancelInvitation] = useMutation(CancelInvitationDocument, {
    onCompleted: () => {
      toaster.success({ title: "Invitation cancelled" })
      refetchInvitations()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to cancel invitation", description: error.message })
    },
  })

  const [resendInvitation] = useMutation(ResendInvitationDocument, {
    onCompleted: () => {
      toaster.success({ title: "Invitation resent" })
      refetchInvitations()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to resend invitation", description: error.message })
    },
  })

  // Handlers that receive data from dialog components
  const handleCreateUser = useCallback(
    async (user: { email: string; name: string; organizationId: string; organizationRole: string }) => {
      if (!user.email || !user.organizationId || !user.organizationRole) {
        toaster.error({ title: "Email and organization are required" })
        return
      }

      setCreating(true)
      try {
        // Just send invitation - user will create account when they accept
        await inviteUserToOrganization({
          variables: {
            email: user.email,
            organizationId: user.organizationId,
            role: user.organizationRole,
          },
        })

        toaster.success({
          title: "Invitation sent successfully",
          description: "User will receive an email to join the organization",
        })
        setShowCreateDialog(false)

        // Refresh invitations list if showing
        if (showInvitations) {
          refetchInvitations()
        }
      } catch (error: any) {
        toaster.error({
          title: "Failed to send invitation",
          description: error.message || "Unknown error",
        })
      } finally {
        setCreating(false)
      }
    },
    [inviteUserToOrganization, showInvitations, refetchInvitations]
  )

  const handleAddToOrganization = useCallback(
    (organizationId: string, action: "invite" | "add" = "invite") => {
      if (!selectedUser || !organizationId) {
        toaster.error({ title: "Please select an organization" })
        return
      }

      if (action === "invite") {
        // Use GraphQL mutation to create invitation (bypasses better-auth permission checks for admins)
        inviteUserToOrganization({
          variables: {
            email: selectedUser.email,
            organizationId: organizationId,
            role: "member",
          },
        })
      } else {
        // Add user directly to organization
        addUserToOrganization({
          variables: {
            userId: selectedUser.id,
            organizationId: organizationId,
            role: "member",
          },
        })
      }
    },
    [selectedUser, inviteUserToOrganization, addUserToOrganization]
  )

  const handleBanUser = useCallback(
    async (reason: string) => {
      if (!selectedUser) return

      setBanning(true)
      try {
        // Call Better Auth admin.banUser directly
        await authClient.admin.banUser({
          userId: selectedUser.id,
          banReason: reason || undefined,
          banExpiresIn: undefined, // Permanent ban
        })

        toaster.success({ title: "User banned successfully" })
        setShowBanDialog(false)
        setSelectedUser(null)
        refetchUsers()
      } catch (error: any) {
        toaster.error({
          title: "Failed to ban user",
          description: error.message || "Unknown error",
        })
      } finally {
        setBanning(false)
      }
    },
    [selectedUser, refetchUsers]
  )

  const handleUnbanUser = useCallback(
    async (user: any) => {
      setUnbanning(true)
      try {
        // Call Better Auth admin.unbanUser directly
        await authClient.admin.unbanUser({
          userId: user.id,
        })

        toaster.success({ title: "User unbanned successfully" })
        refetchUsers()
      } catch (error: any) {
        toaster.error({
          title: "Failed to unban user",
          description: error.message || "Unknown error",
        })
      } finally {
        setUnbanning(false)
      }
    },
    [refetchUsers]
  )

  const organizations = organizationsData?.getAllOrganizations || []
  const users = usersData?.getAllUsers || []

  // Filter users by tenant if selected (still using tenant filter for now)
  const filteredUsers = filterTenant ? users.filter((u: any) => u.tenants && u.tenants.includes(filterTenant)) : users

  const organizationCollection = createListCollection({
    items: organizations.map((org: any) => ({ label: org.name, value: org.id })),
  })

  return (
    <Stack gap={4}>
      <HStack justify="space-between">
        <Text fontSize="lg" fontWeight="bold">
          Users
        </Text>
        <HStack>
          <CreateButton onClick={() => setShowCreateDialog(true)} size="sm">
            Invite User
          </CreateButton>
        </HStack>
      </HStack>

      {usersLoading ? (
        <Spinner />
      ) : (
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Email</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Organizations</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredUsers.map((user: any) => (
              <Table.Row key={user.id}>
                <Table.Cell>
                  <Text fontWeight="medium">{user.name}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text>{user.email}</Text>
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={2}>
                    {user.banned ? (
                      <Badge colorPalette="red">Banned</Badge>
                    ) : (
                      <Badge colorPalette="green">Active</Badge>
                    )}
                    {user.emailVerified ? (
                      <Badge colorPalette="blue">Verified</Badge>
                    ) : (
                      <Badge colorPalette="gray">Unverified</Badge>
                    )}
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="xs">{user.organizations?.length || 0} organization(s)</Text>
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={2}>
                    <AssignButton
                      size="xs"
                      onClick={() => {
                        setSelectedUser(user)
                        setShowAddToOrganizationDialog(true)
                      }}
                    >
                      Add to Organization
                    </AssignButton>
                    {user.banned ? (
                      <Button
                        size="xs"
                        colorPalette="green"
                        variant="outline"
                        onClick={() => handleUnbanUser(user)}
                        loading={unbanning}
                      >
                        <FaCheck />
                        <Text ml={1}>Unban</Text>
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        colorPalette="red"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(user)
                          setShowBanDialog(true)
                        }}
                      >
                        <FaBan />
                        <Text ml={1}>Ban</Text>
                      </Button>
                    )}
                  </HStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      {/* Invitation Management Section */}
      <Box borderTop="1px solid" borderColor="border" pt={6} display={"none"}>
        <VStack align="stretch" gap={4}>
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="bold">
              Organization Invitations
            </Text>
            <HStack>
              <Button
                size="sm"
                variant={showInvitations ? "solid" : "outline"}
                onClick={() => setShowInvitations(!showInvitations)}
              >
                <FaEye />
                {showInvitations ? "Hide" : "Show"} Invitations
              </Button>
            </HStack>
          </HStack>

          {showInvitations && (
            <VStack align="stretch" gap={4}>
              <HStack>
                <Box minW="200px">
                  <NativeSelectRoot collection={organizationCollection}>
                    <NativeSelectField
                      placeholder="Filter by organization"
                      value={selectedOrgForInvitations}
                      onChange={(e) => setSelectedOrgForInvitations(e.target.value)}
                    />
                  </NativeSelectRoot>
                </Box>
                <Button size="sm" variant="outline" onClick={() => setSelectedOrgForInvitations("")}>
                  Clear Filter
                </Button>
              </HStack>

              {invitationsLoading ? (
                <Spinner />
              ) : (
                <Box overflowX="auto">
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Email</Table.ColumnHeader>
                        <Table.ColumnHeader>Organization</Table.ColumnHeader>
                        <Table.ColumnHeader>Role</Table.ColumnHeader>
                        <Table.ColumnHeader>Status</Table.ColumnHeader>
                        <Table.ColumnHeader>Expires At</Table.ColumnHeader>
                        <Table.ColumnHeader>Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {(invitationsData?.getAllInvitations || []).map((invitation: any) => (
                        <Table.Row key={invitation.id}>
                          <Table.Cell>
                            <Text fontWeight="medium">{invitation.email}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text>{invitation.organizationName}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge size="sm" variant="outline">
                              {invitation.role}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge
                              size="sm"
                              colorPalette={
                                invitation.status === "pending"
                                  ? "orange"
                                  : invitation.status === "accepted"
                                  ? "green"
                                  : invitation.status === "expired"
                                  ? "red"
                                  : "gray"
                              }
                            >
                              {invitation.status}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Text fontSize="sm">{new Date(invitation.expiresAt).toLocaleDateString()}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <HStack gap={1}>
                              {invitation.status === "pending" && (
                                <>
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    colorPalette="blue"
                                    onClick={() => resendInvitation({ variables: { invitationId: invitation.id } })}
                                  >
                                    <FaEnvelope />
                                  </Button>
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    colorPalette="red"
                                    onClick={() => cancelInvitation({ variables: { invitationId: invitation.id } })}
                                  >
                                    <FaBan />
                                  </Button>
                                </>
                              )}
                            </HStack>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>

                  {(!invitationsData?.getAllInvitations || invitationsData.getAllInvitations.length === 0) && (
                    <Box textAlign="center" py={4}>
                      <Text color="gray.500">
                        {selectedOrgForInvitations
                          ? "No invitations found for the selected organization."
                          : "No invitations found."}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </VStack>
          )}
        </VStack>
      </Box>

      {/* Create User Dialog */}
      <Dialog
        title="Invite New User"
        open={showCreateDialog}
        setOpen={setShowCreateDialog}
        loading={creating}
        disabled={creating}
      >
        <InviteUserDialogContent
          organizationCollection={organizationCollection}
          creating={creating}
          onSubmit={handleCreateUser}
          onCancel={() => setShowCreateDialog(false)}
        />
      </Dialog>

      {/* Add to Organization Dialog */}
      <Dialog
        title="Add User to Organization"
        open={showAddToOrganizationDialog}
        setOpen={setShowAddToOrganizationDialog}
        loading={inviting || adding}
        disabled={inviting || adding}
      >
        <AddToOrganizationDialogContent
          selectedUser={selectedUser}
          organizationCollection={organizationCollection}
          inviting={inviting}
          adding={adding}
          onSubmit={handleAddToOrganization}
          onCancel={() => setShowAddToOrganizationDialog(false)}
        />
      </Dialog>

      {/* Ban User Dialog */}
      <Dialog title="Ban User" open={showBanDialog} setOpen={setShowBanDialog} loading={banning} disabled={banning}>
        <BanUserDialogContent
          selectedUser={selectedUser}
          banning={banning}
          onSubmit={handleBanUser}
          onCancel={() => setShowBanDialog(false)}
        />
      </Dialog>
    </Stack>
  )
}
